<template>
  <div class="miembros-vencidos-container">
    <!-- Header de la sección -->
    <v-card flat class="header-card mb-4">
      <v-card-text>
        <div class="section-header">
          <div class="header-info">
            <v-icon large color="warning" class="mr-3">mdi-calendar-alert</v-icon>
            <div>
              <h2 class="section-title">Miembros Vencidos</h2>
              <p class="section-description">
                Consulta miembros cuyas membresías han vencido en un período específico
              </p>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Filtros mejorados -->
    <v-card class="filter-card mb-4" elevation="2">
      <v-card-title class="filter-title">
        <v-icon left color="primary">mdi-filter</v-icon>
        Filtros de Búsqueda
      </v-card-title>
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filtrosMiembrosVencidos.fecha_inicio"
              label="Fecha de inicio"
              type="date"
              outlined
              dense
              hide-details="auto"
              prepend-inner-icon="mdi-calendar-start"
              hint="Fecha desde la cual buscar vencimientos"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filtrosMiembrosVencidos.fecha_fin"
              label="Fecha de fin (opcional)"
              type="date"
              outlined
              dense
              hide-details="auto"
              prepend-inner-icon="mdi-calendar-end"
              hint="Deja vacío para buscar desde fecha inicio hasta hoy"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center">
            <v-btn 
              color="warning" 
              @click="obtenerReporteMiembrosVencidos"
              :loading="cargandoMiembrosVencidos"
              :disabled="cargandoMiembrosVencidos"
              large
              block
              elevation="2"
            >
              <v-icon left>mdi-magnify</v-icon>
              Buscar Miembros
            </v-btn>
          </v-col>
        </v-row>
        
        <!-- Indicadores de estado -->
        <v-row class="mt-3" v-if="busquedaMiembrosVencidosRealizada || reporteMiembrosVencidos.length > 0">
          <v-col cols="12">
            <div class="d-flex flex-wrap gap-2">
              <v-chip 
                color="info" 
                outlined 
                v-if="reporteMiembrosVencidos.length === 0 && !cargandoMiembrosVencidos && !busquedaMiembrosVencidosRealizada"
              >
                <v-icon left small>mdi-information</v-icon>
                Configura las fechas y presiona buscar
              </v-chip>
              <v-chip 
                color="success" 
                v-if="reporteMiembrosVencidos.length > 0"
              >
                <v-icon left small>mdi-check-circle</v-icon>
                {{ reporteMiembrosVencidos.length }} miembro(s) encontrado(s)
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Tabla de resultados mejorada -->
    <v-card elevation="2" class="results-card">
      <v-card-title class="results-title">
        <v-icon left color="error">mdi-account-clock</v-icon>
        Resultados de Búsqueda
      </v-card-title>
      <v-divider></v-divider>
      
      <v-data-table
        :headers="headersMiembrosVencidos"
        :items="reporteMiembrosVencidos"
        class="data-table-custom"
        :footer-props="{
          itemsPerPageText: 'Filas por página:',
          itemsPerPageOptions: [10, 25, 50, 100]
        }"
        :loading="cargandoMiembrosVencidos"
        loading-text="Buscando miembros vencidos..."
        no-data-text="No hay miembros vencidos en este período"
        :items-per-page="25"
      >
        <!-- Slot personalizado para fecha de vencimiento -->
        <template v-slot:item.fechaFin="{ item }">
          <div class="fecha-vencimiento">
            <v-chip 
              small
              :color="getDiasVencido(item.fechaFin) > 30 ? 'error' : 'warning'"
              text-color="white"
              class="font-weight-medium"
            >
              <v-icon small left>mdi-calendar-clock</v-icon>
              {{ formatearFechaSoloFecha(item.fechaFin) }}
            </v-chip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon 
                  small 
                  class="ml-1 info-icon" 
                  v-bind="attrs" 
                  v-on="on"
                  color="grey darken-1"
                >
                  mdi-information-outline
                </v-icon>
              </template>
              <span>Vencido hace {{ getDiasVencido(item.fechaFin) }} día(s)</span>
            </v-tooltip>
          </div>
        </template>
        
        <!-- Slot personalizado para tipo de membresía -->
        <template v-slot:item.tipo_membresia="{ item }">
          <v-chip 
            small 
            color="primary" 
            outlined
            class="font-weight-medium"
          >
            <v-icon small left>mdi-card-account-details</v-icon>
            {{ item.tipo_membresia || 'Sin membresía' }}
          </v-chip>
        </template>
        
        <!-- Slot personalizado para estado -->
        <template v-slot:item.estado="{ item }">
          <v-chip 
            small 
            color="error"
            class="font-weight-bold"
          >
            <v-icon small left>mdi-alert-circle</v-icon>
            {{ item.estado }}
          </v-chip>
        </template>
        
        <!-- Slot personalizado para matrícula -->
        <template v-slot:item.matricula="{ item }">
          <span class="font-weight-bold text-primary">{{ item.matricula }}</span>
        </template>
        
        <!-- Slot personalizado para nombre -->
        <template v-slot:item.nombre="{ item }">
          <div class="member-name">
            <v-icon small left color="grey darken-1">mdi-account</v-icon>
            <span class="font-weight-medium">{{ item.nombre }}</span>
          </div>
        </template>
        
        <!-- Slot personalizado para teléfono -->
        <template v-slot:item.telefono="{ item }">
          <div class="phone-number" v-if="item.telefono">
            <v-icon small left color="grey darken-1">mdi-phone</v-icon>
            <span>{{ item.telefono }}</span>
          </div>
          <span v-else class="text--disabled">Sin teléfono</span>
        </template>
      </v-data-table>
    </v-card>

    <v-snackbar
      v-model="mostrarMensaje"
      :timeout="3000"
      :color="mensaje.color"
      top
    >
      {{ mensaje.texto }}
    </v-snackbar>
  </div>
</template>

<script>
import Utiles from "../../Servicios/Utiles";

export default {
  name: "MiembrosVencidos",

  data: () => ({
    mostrarMensaje: false,
    mensaje: {
      texto: '',
      color: ''
    },
    // Variables para el reporte de miembros vencidos
    filtrosMiembrosVencidos: {
      fecha_inicio: '',
      fecha_fin: ''
    },
    reporteMiembrosVencidos: [],
    headersMiembrosVencidos: [
      { text: 'Matrícula', value: 'matricula' },
      { text: 'Nombre', value: 'nombre' },
      { text: 'Teléfono', value: 'telefono' },
      { text: 'Tipo de Membresía', value: 'tipo_membresia' },
      { text: 'Fecha de Vencimiento', value: 'fechaFin' },
      { text: 'Estado', value: 'estado' }
    ],
    cargandoMiembrosVencidos: false,
    busquedaMiembrosVencidosRealizada: false,
  }),

  mounted() {
    // Inicializar fechas para miembros vencidos
    // Para miembros vencidos, establecemos los últimos 30 días como rango inicial
    const hace30Dias = new Date();
    // Ajustar a zona horaria de Colombia (UTC-5) antes de calcular
    hace30Dias.setHours(hace30Dias.getHours() - 5);
    hace30Dias.setDate(hace30Dias.getDate() - 30);
    this.filtrosMiembrosVencidos.fecha_inicio = hace30Dias.toISOString().slice(0, 10);
    this.filtrosMiembrosVencidos.fecha_fin = this.obtenerFechaLocal();
  },

  methods: {
    // Método para obtener la fecha local en formato YYYY-MM-DD
    obtenerFechaLocal() {
      const ahora = new Date();
      // Ajustar a zona horaria de Colombia (UTC-5)
      ahora.setHours(ahora.getHours() - 5);
      return ahora.toISOString().slice(0, 10);
    },

    onMostrarMensaje(mensaje) {
      this.mensaje = mensaje
      this.mostrarMensaje = true
    },

    async obtenerReporteMiembrosVencidos() {
      // Validar que al menos fecha_inicio esté seleccionada
      if (!this.filtrosMiembrosVencidos.fecha_inicio) {
        this.onMostrarMensaje({ 
          texto: 'Por favor selecciona al menos la fecha de inicio', 
          color: 'warning' 
        });
        return;
      }

      this.cargandoMiembrosVencidos = true;
      this.busquedaMiembrosVencidosRealizada = true;
      
      try {
        const res = await fetch(Utiles.generarURL('reporte_miembros_vencidos.php'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.filtrosMiembrosVencidos)
        });
        
        const data = await res.json();
        
        if (data.exito) {
          this.reporteMiembrosVencidos = data.miembros;
          
          if (data.miembros.length === 0) {
            this.onMostrarMensaje({ 
              texto: 'No se encontraron miembros vencidos en el rango de fechas seleccionado', 
              color: 'info' 
            });
          } else {
            this.onMostrarMensaje({ 
              texto: `Se encontraron ${data.miembros.length} miembro(s) vencido(s)`, 
              color: 'success' 
            });
          }
        } else {
          this.reporteMiembrosVencidos = [];
          this.onMostrarMensaje({ 
            texto: data.mensaje || 'Error al obtener el reporte de miembros vencidos', 
            color: 'error' 
          });
        }
      } catch (e) {
        console.error('Error al obtener miembros vencidos:', e);
        this.reporteMiembrosVencidos = [];
        this.onMostrarMensaje({ 
          texto: 'Error de conexión al obtener miembros vencidos', 
          color: 'error' 
        });
      } finally {
        this.cargandoMiembrosVencidos = false;
      }
    },

    // Método auxiliar para calcular los días transcurridos desde el vencimiento
    getDiasVencido(fechaVencimiento) {
      if (!fechaVencimiento) return 0;
      
      const fechaVenc = new Date(fechaVencimiento);
      const fechaActual = new Date();
      
      // Calcular la diferencia en milisegundos
      const diferencia = fechaActual.getTime() - fechaVenc.getTime();
      
      // Convertir a días
      const dias = Math.floor(diferencia / (1000 * 60 * 60 * 24));
      
      return dias > 0 ? dias : 0;
    },

    // Método para formatear fecha sin incluir la hora
    formatearFechaSoloFecha(fecha) {
      if (!fecha) return '';
      
      // Crear fecha sin conversión de zona horaria
      const fechaParts = fecha.split(/[- :]/);
      const fechaLocal = new Date(
        parseInt(fechaParts[0]), // año
        parseInt(fechaParts[1]) - 1, // mes (0-indexado)
        parseInt(fechaParts[2]) // día
      );
      
      return fechaLocal.toLocaleDateString('es-CO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
  },
};
</script>

<style scoped>
/* Container principal */
.miembros-vencidos-container {
  background-color: #f8f9fa;
  min-height: 80vh;
}

/* Header de la sección */
.header-card {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
  color: white;
  border-radius: 12px !important;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-info {
  display: flex;
  align-items: center;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.section-description {
  font-size: 1rem;
  opacity: 0.9;
  margin: 4px 0 0 0;
  font-weight: 300;
}

/* Card de filtros */
.filter-card {
  border-radius: 12px !important;
  border-left: 4px solid #2196f3;
}

.filter-title {
  background-color: #f5f5f5;
  border-radius: 12px 12px 0 0 !important;
  font-weight: 600;
  font-size: 1.1rem;
}

/* Card de resultados */
.results-card {
  border-radius: 12px !important;
  border-left: 4px solid #f44336;
}

.results-title {
  background-color: #f5f5f5;
  border-radius: 12px 12px 0 0 !important;
  font-weight: 600;
  font-size: 1.1rem;
}

/* Tabla personalizada */
.data-table-custom {
  border-radius: 0 0 12px 12px !important;
}

/* Elementos de la tabla */
.fecha-vencimiento {
  display: flex;
  align-items: center;
}

.info-icon {
  cursor: pointer;
  transition: color 0.2s ease;
}

.info-icon:hover {
  color: #2196f3 !important;
}

.member-name {
  display: flex;
  align-items: center;
}

.phone-number {
  display: flex;
  align-items: center;
}

/* Espaciado para chips */
.gap-2 > * {
  margin-right: 8px;
  margin-bottom: 4px;
}

/* Responsive */
@media (max-width: 768px) {
  .section-title {
    font-size: 1.25rem;
  }
  
  .section-description {
    font-size: 0.9rem;
  }
  
  .filter-title,
  .results-title {
    font-size: 1rem;
  }
}

/* Animaciones suaves */
.v-card {
  transition: all 0.3s ease;
}

.v-chip {
  transition: all 0.2s ease;
}

.v-btn {
  transition: all 0.2s ease;
}

/* Estados hover */
.v-card:hover {
  transform: translateY(-2px);
}
</style>



// WEBPACK FOOTER //
// src/components/Miembros/MiembrosVencidos.vue